import React, { useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GoogleLogo, Envelope, Lock, User as UserIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import ReCAPTCHA from 'react-google-recaptcha';
import { trackSignUpStart, trackSignUp } from '../lib/analytics';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);
  const signupStartFired = useRef(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });

  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where did the user come from? If they were sent here while trying to buy
  // (add-to-cart, or the protected cart/checkout pages), treat it as purchase intent.
  const fromPath = location.state?.from?.pathname;
  const purchaseIntent =
    sessionStorage.getItem('auth_intent') === 'from_purchase' ||
    fromPath === '/cart' ||
    fromPath === '/checkout';
  const sourceLabel = purchaseIntent ? 'from_purchase' : 'direct';
  // Return to previous protected page if redirected here; purchase intent goes to cart.
  const postAuthTarget = purchaseIntent ? '/cart' : (fromPath || '/dashboard');

  const handleGoogleAuth = () => {
    // Persist intent so the OAuth round-trip (AuthCallback) can honour it.
    sessionStorage.setItem('auth_intent', purchaseIntent ? 'from_purchase' : 'direct');
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Fire the "started filling the sign-up form" event once per visit (signup mode only).
  const maybeTrackSignUpStart = () => {
    if (isLogin || signupStartFired.current) return;
    if (sessionStorage.getItem('signup_start_fired') === '1') return;
    signupStartFired.current = true;
    sessionStorage.setItem('signup_start_fired', '1');
    trackSignUpStart(sourceLabel);
  };

  const resetCaptcha = () => {
    setCaptchaToken(null);
    if (captchaRef.current) captchaRef.current.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      toast.error("Please complete the 'I'm not a robot' check");
      return;
    }
    setLoading(true);

    try {
      if (isLogin) {
        await login({ email: formData.email, password: formData.password, recaptcha_token: captchaToken });
        toast.success('Welcome back!');
      } else {
        await register({ ...formData, recaptcha_token: captchaToken });
        trackSignUp(sourceLabel, 'email');
        toast.success('Account created successfully!');
      }
      sessionStorage.removeItem('auth_intent');
      navigate(postAuthTarget, { replace: true });
    } catch (error) {
      // Email not registered during login -> switch to signup with email pre-filled.
      if (isLogin && error.response?.status === 404) {
        toast.info("No account found for that email. Let's create one.");
        setIsLogin(false);
        setFormData((prev) => ({ ...prev, password: '', name: '' }));
        resetCaptcha();
        return;
      }
      const message = error.response?.data?.detail || (isLogin ? 'Login failed' : 'Registration failed');
      toast.error(message);
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4" data-testid="auth-page">
      <div className="max-w-md w-full">
        <div className="bg-white border border-border p-8">
          <div className="text-center mb-8">
            <h1 
              className="text-3xl font-black text-primary mb-2"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </h1>
            <p className="text-text_muted">
              {isLogin ? 'Sign in to access your account' : 'Sign up to start building'}
            </p>
          </div>

          {/* Google OAuth Button */}
          <button
            onClick={handleGoogleAuth}
            className="w-full flex items-center justify-center space-x-3 border-2 border-border py-3 rounded hover:bg-gray-50 transition-colors mb-6"
            data-testid="google-auth-btn"
          >
            <GoogleLogo size={24} weight="bold" />
            <span className="font-medium">Continue with Google</span>
          </button>

          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-text_muted">Or continue with email</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-primary mb-2">Name</label>
                <div className="relative">
                  <UserIcon size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text_muted" />
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => { maybeTrackSignUpStart(); setFormData({ ...formData, name: e.target.value }); }}
                    className="w-full pl-10 pr-4 py-3 border border-border rounded focus:ring-2 focus:ring-accent focus:border-accent"
                    placeholder="Your name"
                    required={!isLogin}
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-primary mb-2">Email</label>
              <div className="relative">
                <Envelope size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text_muted" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => { maybeTrackSignUpStart(); setFormData({ ...formData, email: e.target.value }); }}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded focus:ring-2 focus:ring-accent focus:border-accent"
                  placeholder="you@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-2">Password</label>
              <div className="relative">
                <Lock size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-text_muted" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => { maybeTrackSignUpStart(); setFormData({ ...formData, password: e.target.value }); }}
                  className="w-full pl-10 pr-4 py-3 border border-border rounded focus:ring-2 focus:ring-accent focus:border-accent"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <div className="flex justify-center pt-2" data-testid="recaptcha-wrapper">
              {RECAPTCHA_SITE_KEY ? (
                <ReCAPTCHA
                  ref={captchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => setCaptchaToken(token)}
                  onExpired={() => setCaptchaToken(null)}
                />
              ) : (
                <div className="text-xs text-text_muted">reCAPTCHA not configured</div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !captchaToken}
              className="w-full bg-primary text-white py-3 rounded font-medium hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="mt-6 text-center text-sm">
            <button
              onClick={() => { setIsLogin(!isLogin); resetCaptcha(); }}
              className="text-accent hover:underline"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-text_muted">
          <Link to="/" className="hover:text-accent transition-colors">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
